class ProfessionalHealthcareBenefitsComponent extends HTMLElement {
  /** @type {any | null} */
  swiperInstance = null;

  /** @type {AbortController | null} */
  abortController = null;

  /** @type {HTMLElement | null} */
  viewport = null;

  /** @type {MediaQueryList | null} */
  mediaQuery = null;

  /** @type {MediaQueryList | null} */
  tabletMediaQuery = null;

  /** @type {(event: MediaQueryListEvent) => void} */
  mediaQueryListener = () => {};

  /** @type {number} */
  currentSlideIndex = 0;

  /** @type {number | null} */
  originalSlideCount = null;

  connectedCallback() {
    this.setupBreakpointListener();
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  /** @returns {boolean} */
  isMobileViewport() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  /** @returns {number} */
  getSlideCount() {
    return this.querySelectorAll('.professional-healthcare-benefits__slide:not(.slide-clone)').length;
  }

  /** @returns {number} */
  getOriginalSlideCount() {
    if (this.originalSlideCount != null) return this.originalSlideCount;
    return this.getSlideCount();
  }

  /** @returns {number} */
  getSlidesPerViewForViewport() {
    if (this.isMobileViewport()) return 1.2;
    if (window.matchMedia('(max-width: 1023px)').matches) return 3;
    return 4;
  }

  teardownSwiper() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.swiperInstance && typeof this.swiperInstance.destroy === 'function') {
      this.swiperInstance.destroy(true, true);
    }

    this.swiperInstance = null;
    this.currentSlideIndex = 0;
    this.originalSlideCount = null;
    this.viewport = null;
    this.classList.remove('professional-healthcare-benefits--carousel');
    this.classList.remove('professional-healthcare-benefits--swiper');
    this.classList.remove('professional-healthcare-benefits--grid');
  }

  destroy() {
    this.teardownSwiper();

    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.mediaQueryListener);
      this.mediaQuery = null;
    }

    if (this.tabletMediaQuery) {
      this.tabletMediaQuery.removeEventListener('change', this.mediaQueryListener);
      this.tabletMediaQuery = null;
    }
  }

  init() {
    this.teardownSwiper();

    const viewport = this.querySelector('.professional-healthcare-benefits__viewport');
    if (!(viewport instanceof HTMLElement)) return;
    this.viewport = viewport;

    const wrapper = viewport.querySelector('.professional-healthcare-benefits__wrapper, .swiper-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    wrapper.querySelectorAll('.slide-clone').forEach((el) => el.remove());

    let slides = Array.from(wrapper.querySelectorAll('.professional-healthcare-benefits__slide'));
    const slideCount = slides.length;
    this.originalSlideCount = slideCount;

    const prevEl = this.querySelector('.professional-healthcare-benefits__nav--prev');
    const nextEl = this.querySelector('.professional-healthcare-benefits__nav--next');
    const counterEl = this.querySelector('.professional-healthcare-benefits__counter');
    const paginationEl = this.querySelector('.professional-healthcare-benefits__pagination');
    const footerEl = this.querySelector('.professional-healthcare-benefits__footer');
    const prevButton = prevEl instanceof HTMLButtonElement ? prevEl : null;
    const nextButton = nextEl instanceof HTMLButtonElement ? nextEl : null;
    const counter = counterEl instanceof HTMLElement ? counterEl : null;
    const pagination = paginationEl instanceof HTMLElement ? paginationEl : null;
    const footer = footerEl instanceof HTMLElement ? footerEl : null;

    const useSwiper = this.shouldUseSwiper();

    if (!useSwiper) {
      this.classList.add('professional-healthcare-benefits--grid');
      if (prevButton) prevButton.setAttribute('hidden', '');
      if (nextButton) nextButton.setAttribute('hidden', '');
      if (footer) footer.setAttribute('hidden', '');
      return;
    }

    this.classList.add('professional-healthcare-benefits--carousel');
    this.classList.add('professional-healthcare-benefits--swiper');

    const SwiperCtor = /** @type {any} */ (window).Swiper;
    if (!SwiperCtor) return;

    this.abortController = new AbortController();

    const isMobile = this.isMobileViewport();
    const useClones = isMobile && slideCount > 1;

    if (useClones) {
      const clonesNeeded = slideCount <= 3 ? 3 : 1;
      for (let i = 0; i < clonesNeeded; i++) {
        slides.forEach((slide) => {
          const clone = slide.cloneNode(true);
          if (clone instanceof HTMLElement) clone.classList.add('slide-clone');
          wrapper.appendChild(clone);
        });
      }
    }

    const totalSlides = wrapper.querySelectorAll('.professional-healthcare-benefits__slide').length;

    let slidesPerViewCurrent = 1.2;
    if (window.innerWidth >= 1024) slidesPerViewCurrent = 4;
    else if (window.innerWidth >= 769) slidesPerViewCurrent = 3;

    const totalEffectiveSlides = useClones ? totalSlides : slideCount;
    const shouldLoop = (totalEffectiveSlides > slidesPerViewCurrent) || (isMobile && slideCount > 1);

    if (footer) footer.removeAttribute('hidden');

    this.swiperInstance = new SwiperCtor(viewport, {
      containerModifierClass: 'professional-healthcare-benefits-swiper-',
      wrapperClass: 'professional-healthcare-benefits__wrapper',
      slideClass: 'professional-healthcare-benefits__slide',
      slideActiveClass: 'professional-healthcare-benefits__slide--active',
      slideVisibleClass: 'professional-healthcare-benefits__slide--visible',
      slideFullyVisibleClass: 'professional-healthcare-benefits__slide--fully-visible',
      loop: shouldLoop,
      ...(useClones ? { loopedSlides: totalEffectiveSlides } : {}),
      watchOverflow: true,
      observer: false,
      observeParents: false,
      observeSlideChildren: false,
      slidesPerView: 1.2,
      spaceBetween: 16,
      centeredSlides: true,
      centeredSlidesBounds: false,
      grabCursor: true,
      navigation:
        prevButton && nextButton
          ? {
              prevEl: prevButton,
              nextEl: nextButton,
            }
          : undefined,
      breakpoints: {
        769: {
          slidesPerView: 3,
          spaceBetween: 32,
          centeredSlides: false,
          grabCursor: false,
        },
        1024: {
          slidesPerView: 4,
          spaceBetween: 32,
          centeredSlides: false,
          grabCursor: false,
        },
      },
      on: {
        init: (/** @type {any} */ swiper) => {
          this.currentSlideIndex = swiper.realIndex % this.getOriginalSlideCount();
          this.updateCounter(counter, slideCount);
          if (pagination) this.createCustomPagination(pagination, slideCount, swiper);
          this.syncNavState(swiper, prevButton, nextButton);
        },
        resize: (/** @type {any} */ swiper) => {
          this.currentSlideIndex = swiper.realIndex % this.getOriginalSlideCount();
          this.updateCounter(counter, slideCount);
          if (pagination) this.updateCustomPagination(pagination, slideCount, swiper);
          this.syncNavState(swiper, prevButton, nextButton);
        },
        slideChange: (/** @type {any} */ swiper) => {
          this.currentSlideIndex = swiper.realIndex % this.getOriginalSlideCount();
          this.updateCounter(counter, slideCount);
          if (pagination) this.updateCustomPagination(pagination, slideCount, swiper);
          this.syncNavState(swiper, prevButton, nextButton);
        },
      },
    });

    if (prevButton) {
      prevButton.removeAttribute('hidden');
    }

    if (nextButton) {
      nextButton.removeAttribute('hidden');
    }
  }

  /** @returns {boolean} */
  shouldUseSwiper() {
    const slideCount = this.getSlideCount();
    if (slideCount <= 1) return false;

    const visibleSlides = this.getSlidesPerViewForViewport();
    return slideCount > visibleSlides;
  }

  /**
   * @param {any} swiper
   * @param {HTMLButtonElement | null} prevButton
   * @param {HTMLButtonElement | null} nextButton
   */
  syncNavState(swiper, prevButton, nextButton) {
    if (!prevButton || !nextButton) return;

    if (swiper.params?.loop) {
      prevButton.disabled = false;
      nextButton.disabled = false;
      return;
    }

    prevButton.disabled = Boolean(swiper.isBeginning);
    nextButton.disabled = Boolean(swiper.isEnd);
  }

  /**
   * @param {HTMLElement} paginationEl
   * @param {number} slideCount
   * @param {any} swiper
   */
  createCustomPagination(paginationEl, slideCount, swiper) {
    paginationEl.innerHTML = '';

    for (let i = 0; i < slideCount; i++) {
      const bullet = document.createElement('button');
      bullet.className = 'swiper-pagination-bullet';
      bullet.setAttribute('data-index', String(i));
      bullet.setAttribute('aria-label', `Go to slide ${i + 1}`);

      bullet.addEventListener('click', () => {
        if (swiper.params?.loop && typeof swiper.slideToLoop === 'function') {
          swiper.slideToLoop(i);
        } else {
          swiper.slideTo(i);
        }
      });

      paginationEl.appendChild(bullet);
    }

    this.updateCustomPagination(paginationEl, slideCount, swiper);
  }

  /**
   * @param {HTMLElement} paginationEl
   * @param {number} slideCount
   * @param {any} swiper
   */
  updateCustomPagination(paginationEl, slideCount, swiper) {
    const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
    const realIndex = swiper.realIndex % slideCount;

    bullets.forEach((bullet, index) => {
      if (!(bullet instanceof HTMLElement)) return;
      if (index === realIndex) bullet.classList.add('swiper-pagination-bullet-active');
      else bullet.classList.remove('swiper-pagination-bullet-active');
    });
  }

  /**
   * @param {HTMLElement | null} counterEl
   * @param {number} total
   */
  updateCounter(counterEl, total) {
    if (!(counterEl instanceof HTMLElement)) return;
    const current = (this.currentSlideIndex % total) + 1;
    const format = (/** @type {number} */ n) => String(n).padStart(2, '0');
    counterEl.textContent = `${format(current)}/${format(total)}`;
  }

  setupBreakpointListener() {
    this.mediaQuery = window.matchMedia('(max-width: 768px)');
    this.tabletMediaQuery = window.matchMedia('(max-width: 1023px)');

    this.mediaQueryListener = () => {
      this.init();
    };

    this.mediaQuery.addEventListener('change', this.mediaQueryListener);
    this.tabletMediaQuery.addEventListener('change', this.mediaQueryListener);
  }
}

if (!customElements.get('professional-healthcare-benefits-component')) {
  customElements.define('professional-healthcare-benefits-component', ProfessionalHealthcareBenefitsComponent);
}